import { browserTabHidden, browserTabVisible } from '@Helpers/browser'
import { action, computed, observable, runInAction } from 'mobx'
import { BaseStore, getOrCreateStore } from '../../di'
import { Types } from './constants'

const POLLING_INTERVAL = 60 * 1000
const IDLE_INTERVAL = 60 * 1000

class ActivitiesStore extends BaseStore {
  @observable _pollTimer
  @observable _idleTimer
  @observable _status = ''

  // Start: From original Activity Store =======
  @observable activitiesSource = {}
  @observable activitiesDismissed = false
  @observable activities = []
  @observable session = {}

  @action
  setSession = (session) => {
    this.session = session
  }

  @computed
  get latestActivity() {
    return this.activities.length > 0 ? this.activities[0] : null
  }

  @computed
  get latestUnreadActivity() {
    const latest = this.latestActivity
    return latest && !latest.read_at ? latest : null
  }

  @computed
  get unreadCount() {
    if (this.activitiesDismissed) {
      return 0
    } else {
      return this.activities.filter((x) => !x.read_at).length
    }
  }

  @action
  loadActivities(xs) {
    const activities = this.activities
    this.activitiesDismissed = false
    if (activities) {
      activities.replace(xs.map((a) => observable({ ...a })))
    }
  }

  @action
  dismissAllActivitiesStore = () => {
    const { activities } = this
    const session = this.session
    this.activitiesDismissed = true
    if (session && activities.length > 0) {
      const { activity_unread } = session.etc
      if (typeof activity_unread === 'number' && activity_unread > 0) {
        session.etc.activity_read_id = activities[0].id
        session.etc.activity_unread = 0
      }
    }
  }

  @action
  dismissActivityStore(activity, readAt) {
    const idx = this.activities.findIndex((a) => a.id === activity.id)
    this.activities[idx].read_at = readAt
  }

  @action
  acceptActivityStore(activity) {
    const idx = this.activities.findIndex((a) => a.id === activity.id)

    this.activities[idx].etc.user.name = 'You'
    this.activities[idx].etc.invite.status = 'accepted'
    this.activities[idx].created_at = new Date().toISOString()
  }

  @action
  rejectActivityStore(activity) {
    const idx = this.activities.findIndex((a) => a.id === activity.id)

    this.activities[idx].etc.user.name = 'You'
    this.activities[idx].etc.invite.status = 'rejected'
    this.activities[idx].created_at = new Date().toISOString()
  }

  @action
  acceptAdminRequestStore(activity) {
    const idx = this.activities.findIndex((a) => a.id === activity.id)

    this.activities[idx].etc.invite.status = 'orgadmin-approved'
    this.activities[idx].created_at = new Date().toISOString()
  }

  @action
  rejectAdminRequestStore(activity) {
    const idx = this.activities.findIndex((a) => a.id === activity.id)

    this.activities[idx].etc.invite.status = 'orgadmin-rejected'
    this.activities[idx].created_at = new Date().toISOString()
  }

  @action
  setActivitiesStore = (activitiesSource) => {
    this.activitiesSource = activitiesSource
  }
  // End: From original Activity Store =======

  // Should be refactored
  @action
  setJWT = (jwt) => {
    this._jwt = jwt
  }

  setActivitiesSource = async (source, session, jwt) => {
    const oldSource = this.activitiesSource
    this.setSession(session)
    this.setJWT(jwt)

    if (this._hasSourceChange(source, oldSource)) {
      this.setActivitiesStore(source)
      if (this._status === 'active') {
        this.idle()
        if (source) {
          await this.wakeup()
        }
      }
    }
  }

  getSourcePath = () => {
    const source = this.activitiesSource
    if (!source) {
      return `/notifications`
    }

    const { type, id } = source
    switch (type) {
      case 'user':
        return `/notifications`
      case 'hub':
        if (id) {
          return `/hubs/${id}/notifications`
        }
        break
      case 'organization':
        if (id) {
          return `/organizations/${id}/notifications`
        }
    }
    throw Error(`Activities were requested from a source with invalid 'type' and 'id' fields`)
  }

  fetchActivities = async () => {
    if (browserTabHidden()) {
      return
    }
    const path = this.getSourcePath()

    const resp = await this.request({
      path: path,
      query: { limit: 15 },
    })
    this.loadActivities(resp.data)
  }

  handleVisibilityChange = async () => {
    if (browserTabVisible()) {
      await this.wakeup()
    } else {
      this.idle()
    }
  }

  wakeup = async () => {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer)
      runInAction(() => {
        this._idleTimer = null
      })
    }
    if (this._status !== 'active') {
      await this._startPolling()
    }
    this.startTimer()
  }

  startTimer = () => {
    if (this._idleTimer) {
      clearTimeout(this._idleTimer)
    }

    runInAction(() => {
      this._idleTimer = setTimeout(this.idle, IDLE_INTERVAL)
    })
  }

  idle = () => {
    this._stopPolling()
  }

  _startPolling = async () => {
    this._stopPolling()

    runInAction(() => {
      this._status = 'active'
    })

    await this.fetchActivities()

    runInAction(() => {
      this._pollTimer = setInterval(this.fetchActivities, POLLING_INTERVAL)
    })
  }

  _stopPolling = () => {
    if (this._pollTimer) {
      clearInterval(this._pollTimer)

      runInAction(() => {
        this._pollTimer = null
        this._status = 'idle'
      })
    }
  }

  dismissAllActivities = async () => {
    const { latestUnreadActivity } = this
    const path = this.getSourcePath()
    if (latestUnreadActivity) {
      try {
        await this.request({
          method: 'PUT',
          path: path,
          query: {
            until_id: latestUnreadActivity.id,
          },
        })
        this.dismissAllActivitiesStore()
      } catch (e) {
        if (e.status === 409) {
          // ignore
        } else {
          throw e
        }
      }
    }
  }

  dismissActivity = async (activity) => {
    const path = this.getSourcePath()
    try {
      await this.request({
        method: 'PUT',
        path: `${path}/${activity.id}`,
        data: activity,
      })
    } catch (e) {
      if (e.status === 409) {
        // ignore
      } else {
        throw e
      }
    }
    this.dismissActivityStore(activity, new Date().toISOString())
  }

  acceptRequest = async (activity) => {
    if (activity.type === Types.invite) {
      if (activity.hub_id) {
        await this.request({
          method: 'PUT',
          path: `/hubs/${activity.hub_id}/requests/${activity.entity_id}/accept`,
        })
      } else if (activity.org_id) {
        await this.request({
          method: 'PUT',
          path: `/organizations/${activity.org_id}/requests/${activity.entity_id}/accept`,
        })
      }

      this.acceptActivityStore(activity)
    } else if (activity.type === Types.request) {
      if (activity.etc.invite.role === 'orgadmin') {
        try {
          await this.request({
            method: 'PUT',
            path: `/orgadmin/requests/${activity.entity_id}/accept`,
          })
          this.acceptAdminRequestStore(activity)
          // this.AlertsCommands.show(
          //   this.LocaleStore.formatMessage(messages.requestApprovalSuccess),
          //   SUCCESS
          // )
        } catch (e) {
          console.error(e)
          // this.AlertsCommands.show(
          //   this.LocaleStore.formatMessage(messages.requestApprovalError),
          //   DANGER
          // )
        }
      }
    } else {
      return
    }
  }

  rejectRequest = async (activity) => {
    if (activity.type === Types.invite) {
      if (activity.hub_id) {
        await this.request({
          method: 'PUT',
          path: `/hubs/${activity.hub_id}/requests/${activity.entity_id}/reject`,
        })
      } else if (activity.org_id) {
        await this.request({
          method: 'PUT',
          path: `/organizations/${activity.org_id}/requests/${activity.entity_id}/reject`,
        })
      }

      this.rejectActivityStore(activity)
    } else if (activity.type === Types.request) {
      if (activity.etc.invite.role === 'orgadmin') {
        try {
          await this.request({
            method: 'PUT',
            path: `/orgadmin/requests/${activity.entity_id}/reject`,
          })
          this.rejectAdminRequestStore(activity)
          // this.AlertsCommands.show(
          //   this.LocaleStore.formatMessage(messages.requestRejectionSuccess),
          //   SUCCESS
          // )
        } catch (e) {
          console.error(e)
          // this.AlertsCommands.show(
          //   this.LocaleStore.formatMessage(messages.requestRejectionError),
          //   DANGER
          // )
        }
      }
    } else {
      return
    }
  }

  _hasSourceChange = (newSource, oldSource) => {
    return (
      !!newSource !== !!oldSource ||
      (!!newSource &&
        !!oldSource &&
        (newSource.type !== oldSource.type || newSource.id !== oldSource.id))
    )
  }
}

export default getOrCreateStore('ActivitiesStore', ActivitiesStore)
